import React from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

interface Feature {
  icon: string;
  title: string;
  description: string;
  details: string;
  perfectFor: string;
}

const FeatureCard: React.FC<{ feature: Feature; index: number }> = ({ feature, index }) => {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border-t-4 border-soez-blue hover:-translate-y-2 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="w-16 h-16 bg-gradient-to-br from-soez-blue to-soez-blue-dark rounded-full flex items-center justify-center text-3xl mb-6">
        {feature.icon}
      </div>
      <h3 className="text-2xl font-bold text-soez-blue mb-4">{feature.title}</h3>
      <p className="text-gray-600 mb-3 leading-relaxed">{feature.description}</p>
      <p className="text-gray-600 mb-6 leading-relaxed">{feature.details}</p>
      <div className="bg-soez-blue/5 p-4 rounded-lg border-l-4 border-soez-orange">
        <strong className="text-soez-orange text-sm font-semibold">Perfect for:</strong>
        <p className="text-gray-700 text-sm mt-2">{feature.perfectFor}</p>
      </div>
    </div>
  );
};

const Features: React.FC = () => {
  const features: Feature[] = [
    {
      icon: '🔒',
      title: 'Secure & Controlled',
      description:
        'All company conversations happen inside a ringfenced environment with enterprise-grade privacy and precision.',
      details:
        'Admins have full control — managing user access, data retention, and permissions from one dashboard. Even when employees leave, access can be revoked instantly, keeping company data fully contained.',
      perfectFor: 'Corporates, financial institutions, and teams that prioritize confidentiality',
    },
    {
      icon: '💬',
      title: 'Smart Messaging',
      description: 'Clear, private, and organized conversations across your entire organization.',
      details:
        'Chat one-to-one, in teams, or company-wide — with support for voice notes, files, and media sharing. Messages are auto-translated and language-corrected in real time, helping global or multilingual teams stay aligned. Contact details remain hidden by design.',
      perfectFor: 'Schools, corporate groups, and customer communities where privacy matters',
    },
    {
      icon: '🤖',
      title: 'AI Integration',
      description: 'Your built-in company assistant, available 24/7 for instant answers and actions.',
      details:
        'Ask the AI anything — from HR leave policies to finance procedures — and get real-time answers. Trigger instant actions like "Book a boardroom at 2 PM tomorrow" or "Send an update to all staff." If critical alerts are ignored, the AI can escalate with calls or reminders automatically.',
      perfectFor: 'HR, admin, and management teams who value automation and instant insight',
    },
    {
      icon: '⏰',
      title: 'Workforce Tools',
      description: 'Smart attendance and effortless syncing that eliminates manual tracking errors.',
      details:
        'Employees can clock in or out using QR codes or location-based check-ins. Data syncs directly with HR and payroll systems, ensuring records are instant and accurate. This feature helps remove manual tracking errors and streamlines operations.',
      perfectFor: 'Retail, logistics, and distributed teams',
    },
    {
      icon: '📡',
      title: 'Advanced Messaging Modes',
      description: 'Communication designed your way, with precision and control.',
      details:
        'One-to-one: private direct chat. One-to-many: structured broadcast (no messy replies). Many-to-many: dynamic group discussions. All conversations are end-to-end encrypted, traceable, and easy to manage.',
      perfectFor: 'Corporate announcements, leadership messages, and controlled feedback loops',
    },
    {
      icon: '🌍',
      title: 'Language Translation & Correction',
      description: 'Break the language barrier instantly with real-time translation and correction.',
      details:
        'Every message is automatically translated and corrected for clarity in real time — across text, voice, and media captions. Teams can collaborate across countries and accents without missing a word.',
      perfectFor: 'Cross-border companies, NGOs, and multilingual teams',
    },
    {
      icon: '📞',
      title: 'AI Voice & Automation',
      description: 'Your proactive digital PA that ensures no update is ever missed.',
      details:
        "SoEz's AI can send automated voice calls or reminders — from wake-up calls and meeting prompts to critical system alerts. Never miss an update again — even if messages are unread.",
      perfectFor: 'Time-sensitive operations, remote teams, and customer communication',
    },
    {
      icon: '🛡️',
      title: 'Data Ownership & Security Framework',
      description: 'You own the data — always. Complete control, complete privacy.',
      details:
        "Every message is logged, encrypted, and stored within your organization's control. Admins can revoke access immediately post-termination, harvest data responsibly, or enable next-morning data deletion for privacy compliance.",
      perfectFor: 'Regulated industries and security-conscious teams',
    },
  ];

  return (
    <section id="features" className="py-20 px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-soez-blue mb-4">Core Features</h2>
          <div className="w-20 h-1 bg-soez-orange mx-auto mb-6" />
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Every feature designed to protect your data, simplify workflows, and connect people effortlessly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mt-12">
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
